import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertTriangle, Clock, MapPin, Truck, TrendingUp, Activity, UserCheck, CheckCircle, Download } from 'lucide-react';
import { motion } from 'motion/react';
import { safetyService } from '../../utils/services/safetyService';
import { notificationService } from '../../utils/services/notificationService';
import { SafetyAlert, FleetStats } from '../../types/safety';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { useTheme } from '../../hooks/useTheme';
import OrphanWellBanner from './OrphanWellBanner';

interface OperationsDashboardProps {
    onNavigateToFleet?: () => void;
    onNavigateToApprovals?: () => void;
    onNavigateToAssignments?: () => void;
}

export default function OperationsDashboard({ onNavigateToFleet, onNavigateToApprovals, onNavigateToAssignments }: OperationsDashboardProps) {
    const { isDarkMode } = useTheme();
    const [stats, setStats] = useState<FleetStats | null>(null);
    const [alerts, setAlerts] = useState<SafetyAlert[]>([]);
    const [loading, setLoading] = useState(true);

    // Broadcast Alert State
    const [isBroadcastOpen, setIsBroadcastOpen] = useState(false);
    const [broadcastTitle, setBroadcastTitle] = useState('');
    const [broadcastMessage, setBroadcastMessage] = useState('');
    const [broadcastSeverity, setBroadcastSeverity] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
    const [isSending, setIsSending] = useState(false);

    useEffect(() => {
        // Initial Fetch
        const loadData = async () => {
            try {
                const statsData = await safetyService.getFleetStats();
                setStats(statsData);
            } catch (error) {
                console.error('Failed to load fleet stats', error);
            } finally {
                setLoading(false);
            }
        };

        loadData();

        // Real-time Alerts Subscription
        const unsubscribe = safetyService.subscribeToAlerts((newAlerts) => {
            setAlerts(newAlerts);
        });

        return () => unsubscribe();
    }, []);

    const handleDownloadReport = () => {
        if (!stats) {
            toast.error("No data available to export");
            return;
        }

        const reportData = {
            timestamp: new Date().toISOString(),
            activeVehicles: stats.activeVehicles,
            totalAlerts24h: stats.totalAlerts24h,
            safetyScore: stats.safetyScore,
            fuelEfficiency: stats.fuelEfficiency,
            alerts: alerts.map(a => ({
                type: a.type,
                severity: a.severity,
                vehicleId: a.vehicleId,
                timestamp: a.timestamp
            }))
        };

        const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `operations-report-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast.success("Report downloaded successfully");
    };

    const handleBroadcastAlert = async () => {
        if (!broadcastTitle || !broadcastMessage) {
            toast.error("Please fill in all fields");
            return;
        }

        setIsSending(true);
        try {
            await notificationService.broadcastAlert(broadcastTitle, broadcastMessage, broadcastSeverity);
            toast.success("Alert broadcasted to all users");
            setIsBroadcastOpen(false);
            setBroadcastTitle('');
            setBroadcastMessage('');
            setBroadcastSeverity('medium');
        } catch (error) {
            console.error("Failed to broadcast alert:", error);
            toast.error("Failed to broadcast alert");
        } finally {
            setIsSending(false);
        }
    };

    const handleLocateNearestDriver = () => {
        if (stats?.activeVehicles && stats.activeVehicles > 0) {
            // Navigate to fleet map where drivers are already displayed
            if (onNavigateToFleet) {
                onNavigateToFleet();
                toast.success('Opening Fleet Map with active drivers');
            } else {
                toast.info('Fleet Map not available from this view');
            }
        } else {
            toast.info('No active drivers currently. Drivers appear here when they have location tracking enabled.');
        }
    };

    const handleReviewTimecards = () => {
        if (onNavigateToApprovals) {
            onNavigateToApprovals();
        } else {
            toast.info('Navigate to the Approvals tab to review timecards');
        }
    };

    // Metrics with gradient configurations
    const metrics = [
        {
            label: 'Active Fleet',
            value: stats?.activeVehicles.toString() || '0',
            subtext: stats?.activeVehicles === 0 ? 'No drivers online' : 'On the road',
            icon: Truck,
            gradient: 'from-blue-500 via-blue-600 to-cyan-500',
            iconColor: 'text-blue-600',
            bgGradient: 'from-blue-400/20 via-blue-500/20 to-cyan-400/20'
        },
        {
            label: 'Safety Alerts',
            value: stats?.totalAlerts24h.toString() || '0',
            subtext: 'Past 24 hours',
            icon: AlertTriangle,
            gradient: 'from-orange-500 via-orange-600 to-red-500',
            iconColor: 'text-orange-600',
            bgGradient: 'from-orange-400/20 via-orange-500/20 to-red-400/20'
        },
        {
            label: 'Safety Score',
            value: stats ? `${stats.safetyScore}%` : '100%',
            subtext: 'Fleet average',
            icon: UserCheck,
            gradient: 'from-emerald-500 via-emerald-600 to-green-500',
            iconColor: 'text-emerald-600',
            bgGradient: 'from-emerald-400/20 via-emerald-500/20 to-green-400/20'
        },
        {
            label: 'Efficiency',
            value: stats?.fuelEfficiency ? `${stats.fuelEfficiency} MPG` : 'N/A',
            subtext: stats?.fuelEfficiency ? 'Avg Fuel Economy' : 'Not tracked',
            icon: Activity,
            gradient: 'from-amber-500 via-amber-600 to-yellow-500',
            iconColor: 'text-amber-600',
            bgGradient: 'from-amber-400/20 via-amber-500/20 to-yellow-400/20'
        },
    ];

    return (
        <div className="space-y-6">
            {/* Enterprise Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h2 className={`text-3xl font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Operations Command
                    </h2>
                    <p className={`mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button
                            variant="outline"
                            className={`hidden md:flex ${isDarkMode
                                    ? 'border-slate-700 hover:bg-slate-800'
                                    : 'border-gray-200 hover:bg-gray-50'
                                }`}
                            onClick={handleDownloadReport}
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Download Report
                        </Button>
                    </motion.div>
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button
                            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-600/20"
                            onClick={() => setIsBroadcastOpen(true)}
                        >
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            Broadcast Alert
                        </Button>
                    </motion.div>
                </div>
            </div>

            {/* Broadcast Alert Modal */}
            <Dialog open={isBroadcastOpen} onOpenChange={setIsBroadcastOpen}>
                <DialogContent className={isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white'}>
                    <DialogHeader>
                        <DialogTitle className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                            Broadcast Emergency Alert
                        </DialogTitle>
                        <DialogDescription className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                            This will send a high-priority notification to ALL users in your organization.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Alert Title</Label>
                            <Input
                                placeholder="e.g., Severe Weather Warning"
                                value={broadcastTitle}
                                onChange={(e) => setBroadcastTitle(e.target.value)}
                                className={isDarkMode
                                    ? 'bg-slate-800 border-slate-700 text-white'
                                    : 'bg-white border-gray-300'
                                }
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Severity Level</Label>
                            <Select
                                value={broadcastSeverity}
                                onValueChange={(v: 'low' | 'medium' | 'high' | 'critical') => setBroadcastSeverity(v)}
                            >
                                <SelectTrigger className={isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-300'}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className={isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
                                    <SelectItem value="low">Low (Info)</SelectItem>
                                    <SelectItem value="medium">Medium (Warning)</SelectItem>
                                    <SelectItem value="high">High (Urgent)</SelectItem>
                                    <SelectItem value="critical">Critical (Emergency)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Message</Label>
                            <Textarea
                                placeholder="Enter detailed instructions..."
                                value={broadcastMessage}
                                onChange={(e) => setBroadcastMessage(e.target.value)}
                                rows={4}
                                className={isDarkMode
                                    ? 'bg-slate-800 border-slate-700 text-white'
                                    : 'bg-white border-gray-300'
                                }
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsBroadcastOpen(false)}
                            className={isDarkMode ? 'border-slate-700 hover:bg-slate-800' : ''}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white"
                            onClick={handleBroadcastAlert}
                            disabled={isSending}
                        >
                            {isSending ? 'Sending...' : 'Broadcast Now'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* KPI Metrics Grid with Gradient Cards */}
            <div className="grid grid-cols-2 gap-3 mb-6">
                {metrics.map((metric, index) => (
                    <motion.div
                        key={metric.label}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1, type: "spring" }}
                        whileHover={{ scale: 1.05, y: -4 }}
                        whileTap={{ scale: 0.98 }}
                        className={`
                            relative overflow-hidden rounded-3xl
                            ${isDarkMode
                                ? `bg-gradient-to-br ${metric.gradient} p-5`
                                : `bg-gradient-to-br ${metric.bgGradient} backdrop-blur p-1 border-3 border-white/60`
                            }
                        `}
                    >
                        <div className={`
                            ${isDarkMode ? '' : 'bg-white/85 backdrop-blur rounded-[1.3rem] p-5'}
                            text-center aspect-square flex flex-col justify-center
                        `}>
                            {/* Icon with rotation animation */}
                            <motion.div
                                whileHover={{ rotate: 360 }}
                                transition={{ duration: 0.6 }}
                            >
                                <metric.icon className={`w-10 h-10 mx-auto mb-3 ${isDarkMode ? 'text-white' : metric.iconColor
                                    }`} strokeWidth={2.5} />
                            </motion.div>

                            {/* Value with scale animation */}
                            <motion.div
                                className={`text-5xl mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                                initial={{ scale: 1.3, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.4 + index * 0.1 }}
                            >
                                {loading ? <span className="animate-pulse">...</span> : metric.value}
                            </motion.div>

                            {/* Labels */}
                            <div className={`text-sm mb-1 ${isDarkMode ? 'text-white/90' : 'text-gray-700'}`}>
                                {metric.label}
                            </div>
                            <div className={`text-[9px] ${isDarkMode ? 'text-white/80' : 'text-gray-600'}`}>
                                {metric.subtext}
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Orphan Wells Warning Banner */}
            <OrphanWellBanner
                onActionClick={onNavigateToAssignments}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Recent Alerts Feed */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    className={`lg:col-span-2 rounded-3xl ${isDarkMode
                            ? 'bg-slate-800/50 border border-slate-700'
                            : 'bg-white border border-gray-200 shadow-sm'
                        }`}
                >
                    <CardHeader>
                        <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Recent Safety Alerts
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {alerts.length === 0 ? (
                                <div className="text-center py-8">
                                    <motion.div
                                        animate={{
                                            scale: [1, 1.2, 1],
                                            opacity: [1, 0.7, 1]
                                        }}
                                        transition={{
                                            duration: 2,
                                            repeat: Infinity
                                        }}
                                        className="inline-block"
                                    >
                                        <CheckCircle className={`w-8 h-8 mx-auto mb-2 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-500'
                                            }`} />
                                    </motion.div>
                                    <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                                        No active alerts. Fleet is operating safely.
                                    </p>
                                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                        Alerts will appear here when safety issues are detected.
                                    </p>
                                </div>
                            ) : (
                                alerts.map((alert, index) => (
                                    <motion.div
                                        key={alert.id}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className={`rounded-2xl p-4 ${isDarkMode
                                                ? 'bg-slate-800/50 border border-slate-700'
                                                : 'bg-white border border-gray-200 shadow-sm'
                                            }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`
                                                w-10 h-10 rounded-xl flex items-center justify-center
                                                ${alert.severity === 'critical'
                                                    ? isDarkMode
                                                        ? 'bg-gradient-to-br from-red-500 to-rose-500'
                                                        : 'bg-gradient-to-br from-red-400/20 to-rose-400/20 border border-red-300'
                                                    : isDarkMode
                                                        ? 'bg-gradient-to-br from-orange-500 to-red-500'
                                                        : 'bg-gradient-to-br from-orange-400/20 to-red-400/20 border border-orange-300'
                                                }
                                            `}>
                                                <AlertTriangle className={`w-5 h-5 ${isDarkMode ? 'text-white' :
                                                        alert.severity === 'critical' ? 'text-red-600' : 'text-orange-600'
                                                    }`} />
                                            </div>
                                            <div className="flex-1">
                                                <h4 className={`text-sm mb-1 capitalize ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                    {alert.type.replace('_', ' ')}
                                                </h4>
                                                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                                    {alert.driverName || 'Unassigned User'} • {alert.details.description || 'Alert triggered'}
                                                </p>
                                                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                                    {formatDistanceToNow(alert.timestamp, { addSuffix: true })} • {alert.location?.address || 'Unknown Location'}
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))
                            )}
                        </div>
                    </CardContent>
                </motion.div>

                {/* Quick Actions */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                    className={`rounded-3xl ${isDarkMode
                            ? 'bg-slate-800/50 border border-slate-700'
                            : 'bg-white border border-gray-200 shadow-sm'
                        }`}
                >
                    <CardHeader>
                        <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Quick Actions
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            <Button
                                variant="outline"
                                className={`w-full justify-start gap-3 h-12 ${isDarkMode
                                        ? 'border-slate-700 hover:bg-slate-800 text-gray-300'
                                        : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                                onClick={handleLocateNearestDriver}
                            >
                                <MapPin className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                                <div className="text-left flex-1">
                                    <div className={isDarkMode ? 'text-white' : 'text-gray-900'}>Locate Drivers</div>
                                    <div className={`text-xs font-normal ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        {stats?.activeVehicles || 0} active
                                    </div>
                                </div>
                            </Button>
                        </motion.div>

                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            <Button
                                variant="outline"
                                className={`w-full justify-start gap-3 h-12 ${isDarkMode
                                        ? 'border-slate-700 hover:bg-slate-800 text-gray-300'
                                        : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                                onClick={handleReviewTimecards}
                            >
                                <Clock className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-500'}`} />
                                <div className="text-left flex-1">
                                    <div className={isDarkMode ? 'text-white' : 'text-gray-900'}>Review Timecards</div>
                                    <div className={`text-xs font-normal ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        Approve hours & PTO
                                    </div>
                                </div>
                            </Button>
                        </motion.div>

                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            <Button
                                variant="outline"
                                className={`w-full justify-start gap-3 h-12 ${isDarkMode
                                        ? 'border-slate-700 hover:bg-slate-800 text-gray-300'
                                        : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                                onClick={() => setIsBroadcastOpen(true)}
                            >
                                <TrendingUp className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
                                <div className="text-left flex-1">
                                    <div className={isDarkMode ? 'text-white' : 'text-gray-900'}>Send Announcement</div>
                                    <div className={`text-xs font-normal ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        Notify all users
                                    </div>
                                </div>
                            </Button>
                        </motion.div>
                    </CardContent>
                </motion.div>
            </div>
        </div>
    );
}
